import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { campaignsApi, queryKeys, reportsApi } from '../../lib/api/resources';
import { count, date, isoDate, money, percent } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  MetricCard,
  Skeleton,
  StatusBadge,
  Table,
  TableSkeleton,
  Td,
  Th,
} from '../../components/ui';

/**
 * Delivery across the account, filterable by date, with a per-campaign breakdown.
 *
 * <p>The CSV export is generated in the browser from figures the server already computed. It
 * formats; it does not recompute — an export that disagreed with the panel would be worse than no
 * export at all.
 */
export function ReportsPage() {
  const [from, setFrom] = useState(isoDate(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(isoDate(new Date()));

  const range = { from, to };

  const summary = useQuery({
    queryKey: queryKeys.summary(range),
    queryFn: () => reportsApi.summary(range),
  });

  const campaigns = useQuery({
    queryKey: queryKeys.campaigns({ size: 100 }),
    queryFn: () => campaignsApi.list({ size: 100 }),
  });

  const exportCsv = () => {
    const rows = campaigns.data?.content ?? [];
    const header = [
      'Campaña',
      'Anunciante',
      'Estado',
      'Modelo',
      'Presupuesto',
      'Consumido',
      'Restante',
      'Inicio',
      'Fin',
    ];

    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

    const csv = [
      header.map(escape).join(','),
      ...rows.map((campaign) =>
        [
          campaign.name,
          campaign.advertiserName ?? '',
          campaign.status,
          campaign.pricingModel,
          campaign.budgetTotal.toFixed(2),
          campaign.budgetConsumed.toFixed(2),
          campaign.budgetRemaining.toFixed(2),
          campaign.startDate,
          campaign.endDate,
        ]
          .map((value) => escape(String(value)))
          .join(','),
      ),
    ].join('\n');

    // A BOM so Excel opens the accents correctly; without it "Campaña" arrives mangled.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campanas-${from}-a-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-headline-lg text-text-primary">Reportes</h1>

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Desde" htmlFor="from">
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Field>
          <Field label="Hasta" htmlFor="to">
            <Input id="to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <Button onClick={exportCsv} disabled={!campaigns.data}>
            Exportar CSV
          </Button>
        </div>
      </header>

      {summary.isError ? (
        <ErrorState
          message={(summary.error as Error).message}
          onRetry={() => void summary.refetch()}
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {summary.isLoading ? (
              Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28" />)
            ) : (
              <>
                <MetricCard label="Impresiones" value={count(summary.data!.impressions)} />
                <MetricCard label="Clics" value={count(summary.data!.clicks)} />
                <MetricCard label="CTR" value={percent(summary.data!.ctr)} />
                <MetricCard
                  label="Rechazados"
                  value={count(summary.data!.rejectedEvents)}
                  hint="No facturados"
                />
              </>
            )}
          </div>

          <Card title={`Entrega diaria · ${date(from)} — ${date(to)}`}>
            {summary.isLoading ? (
              <Skeleton className="h-64" />
            ) : summary.data!.impressions === 0 ? (
              <EmptyState
                title="Sin entrega en este periodo"
                description="Ajusta el rango de fechas o espera a que una campaña activa empiece a servir."
              />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={summary.data!.daily}>
                  <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                  <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} tickMargin={8} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: '#1E293B',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      color: '#F8FAFC',
                    }}
                  />
                  <Bar dataKey="impressions" name="Impresiones" fill="#0066FF" />
                  <Bar dataKey="clicks" name="Clics" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}

      <Card title="Desglose por campaña" className="p-0">
        {campaigns.isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={5} columns={5} />
          </div>
        ) : campaigns.data!.content.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="Todavía no hay campañas"
              description="El desglose aparecerá cuando exista al menos una campaña."
            />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Campaña</Th>
                <Th>Anunciante</Th>
                <Th>Estado</Th>
                <Th numeric>Consumido</Th>
                <Th numeric>Restante</Th>
              </tr>
            </thead>
            <tbody>
              {campaigns.data!.content.map((campaign) => (
                <tr key={campaign.id}>
                  <Td>{campaign.name}</Td>
                  <Td>{campaign.advertiserName ?? '—'}</Td>
                  <Td>
                    <StatusBadge status={campaign.status} />
                  </Td>
                  <Td numeric>{money(campaign.budgetConsumed)}</Td>
                  <Td numeric>{money(campaign.budgetRemaining)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
